import * as api from '@/api'
import { changeName } from '@/api/user'
import router from '@/router'
import { RawLocation } from 'vue-router'
import Cookies from 'js-cookie'

enum LoginStatus {
  NotLoggedIn = 'NotLoggedIn',
  LoggingIn = 'LoggingIn',
  LoggedIn = 'LoggedIn',
  LoggingOut = 'LoggingOut',
}

enum UserLoadingStatus {
  NotLoaded = 'NotLoaded',
  Loading = 'Loading',
  Loaded = 'Loaded',
}

enum UserUpdatingStatus {
  NotUpdating = 'NotUpdating',
  Updating = 'Updating',
  Updated = 'Updated',
}

interface UserData {
  character: number,
  character_stats: any[]
  characters: number[]
  cores: any[]
  curr_available_maps: any[]
  current_map: string
  display_name: string
  friends: any[]
  is_locked_name_duplicate: boolean
  is_skill_sealed: boolean
  join_date: number
  max_friend: number
  max_stamina_ts: number
  name: string
  next_fragstam_ts: number
  packs: []
  prog_boost: number
  rating: number
  recent_score: any[]
  settings: any
  singles: string[]
  stamina: number
  ticket: number
  user_code: string
  user_id: number
  world_songs: string[]
  world_unlocks: string[],
  country: string
  is_allowed_payment: boolean
  is_email_valid: boolean
  arcaea_online_expire_ts: number
}

interface UserState {
  loginStatus: LoginStatus
  userLoadingStatus: UserLoadingStatus
  userUpdatingStatus: UserUpdatingStatus
  user?: UserData
  error?: Error
  isSupport: boolean
  isShowPurchase: boolean
}

const state: UserState = {
  loginStatus: LoginStatus.NotLoggedIn,
  userLoadingStatus: UserLoadingStatus.NotLoaded,
  userUpdatingStatus: UserUpdatingStatus.NotUpdating,
  user: undefined,
  error: undefined,
  isSupport: false,
  isShowPurchase: false
}

const getters = {
  isLoggedIn: (state: UserState): boolean => state.loginStatus === LoginStatus.LoggedIn,
  isLoggingIn: (state: UserState): boolean => state.loginStatus === LoginStatus.LoggingIn,
  isLoggingOut: (state: UserState): boolean => state.loginStatus === LoginStatus.LoggingOut,
  isAuthUserLoading: (state: UserState): boolean => state.userLoadingStatus === UserLoadingStatus.Loading,
  isAuthUserLoaded: (state: UserState): boolean => state.userLoadingStatus === UserLoadingStatus.Loaded,
  isAuthUserUpdating: (state: UserState): boolean => state.userUpdatingStatus === UserUpdatingStatus.Updating,
  isAuthUserUpdated: (state: UserState): boolean => state.userUpdatingStatus === UserUpdatingStatus.Updated,
  isShowPurchase: (state: UserState): boolean => state.isShowPurchase,
  authError: (state: UserState): Error | undefined => state.error,
  authUser: (state: UserState): UserData | undefined => state.user
}

const mutations = {
  LOGIN(state: UserState) {
    state.loginStatus = LoginStatus.LoggingIn
    state.error = undefined
  },

  LOGIN_SUCCESS(state: UserState, { user }: { user: UserData; }) {
    state.loginStatus = LoginStatus.LoggedIn
    state.user = { ...state.user, ...user }
  },

  LOGIN_FAILURE(state: UserState, error: Error) {
    state.loginStatus = LoginStatus.NotLoggedIn
    state.error = error
  },

  LOGOUT(state: UserState) {
    state.loginStatus = LoginStatus.LoggingOut
    state.error = undefined
  },

  LOGOUT_SUCCESS(state: UserState, option?: { redirectTo: RawLocation }) {
    state.userLoadingStatus = UserLoadingStatus.NotLoaded
    state.loginStatus = LoginStatus.NotLoggedIn
    if (option) {
      router.push(option.redirectTo)
    }
    state.user = undefined
  },

  LOGOUT_FAILURE(state: UserState, error: Error) {
    state.loginStatus = LoginStatus.LoggedIn
    state.error = error
  },

  LOAD_AUTH_USER(state: UserState) {
    state.userLoadingStatus = UserLoadingStatus.Loading
    state.error = undefined
  },

  LOAD_AUTH_USER_SUCCESS(state: UserState, user: UserData | {}) {
    if (!user) {
      state.userLoadingStatus = UserLoadingStatus.NotLoaded
      state.loginStatus = LoginStatus.NotLoggedIn
      state.user = undefined

      return
    }

    state.userLoadingStatus = UserLoadingStatus.Loaded
    state.loginStatus = LoginStatus.LoggedIn
    state.user = { ...state.user, ...(user as UserData) }
  },

  LOAD_AUTH_USER_FAILURE(state: UserState, error: Error) {
    state.userLoadingStatus = UserLoadingStatus.NotLoaded
    state.loginStatus = LoginStatus.NotLoggedIn
    state.error = error
  },

  UPDATE_AUTH_USER(state: UserState) {
    state.userUpdatingStatus = UserUpdatingStatus.Updating
    state.error = undefined
  },

  UPDATE_AUTH_USER_SUCCESS(state: UserState, user: UserData) {
    state.userUpdatingStatus = UserUpdatingStatus.Updated
    state.user = { ...state.user, ...user }
  },

  UPDATE_AUTH_USER_FAILURE(state: UserState, error: Error) {
    state.userUpdatingStatus = UserUpdatingStatus.NotUpdating
    state.error = error
  },

  CHANGE_PASSWORD(state: UserState) {
    state.userUpdatingStatus = UserUpdatingStatus.Updating
    state.error = undefined
  },

  CHANGE_PASSWORD_SUCCESS(state: UserState) {
    state.userUpdatingStatus = UserUpdatingStatus.Updated
  },

  CHANGE_PASSWORD_FAILURE(state: UserState, error: Error) {
    state.userUpdatingStatus = UserUpdatingStatus.NotUpdating
    state.error = error
  },

  CHANGE_NAME(state: UserState, { name }: { name: string; }) {
    state.user!.name = name
  },

  UPDATE_MEMORY(state: UserState, { amount }: { amount: number }) {
    const amt = state.user!.ticket;
    state.user!.ticket = amt + amount
  },

  SET_MEMORY(state: UserState, { amount }: { amount: number }) {
    state.user!.ticket = amount
  },

  UPDATE_SHOW_PURCHASE(state: UserState, value: boolean) {
    state.isShowPurchase = value
  },

  SET_COUNTRY(state: UserState) {
    state.userUpdatingStatus = UserUpdatingStatus.Updating
    state.error = undefined
  },

  SET_COUNTRY_SUCCESS(state: UserState, { country }: { country: string; }) {
    state.userUpdatingStatus = UserUpdatingStatus.Updated
    state.user!.country = country;
  },

  SET_COUNTRY_FAILURE(state: UserState, error: Error) {
    state.userUpdatingStatus = UserUpdatingStatus.NotUpdating
    state.error = error
  },
}

const actions = {
  async login(
    { commit, getters, dispatch }: any,
    { email, password }: { email: string; password: string; }
  ) {
    if (getters.isLoggedIn) {
      await dispatch('logout')
    }

    commit('LOGIN')

    try {
      await api.auth.login({ email, password })
      const user = await api.user.me()
      commit('LOGIN_SUCCESS', { user })
    } catch (error) {
      commit('LOGIN_FAILURE', error)
      throw error
    }
  },

  async logout({ commit, getters, dispatch }: any, option?: { redirectTo: RawLocation }) {
    if (!getters.isLoggedIn) {
      return
    }

    commit('LOGOUT')

    return api.auth.logout().then(async () => {
        commit('LOGOUT_SUCCESS', option)

        // avoid invalid csrf token
        await dispatch('loadAuthUser').catch(() => {
          // Do nothing
        })
      })
      .catch((error) => {
        commit('LOGOUT_FAILURE', error)

        throw error
      })
  },

  async updateUserCountry(
    { commit, getters, dispatch }: any,
    { country }: { country: string; }
  ) {
    commit('SET_COUNTRY');

    try {
      await api.user.setCountry(country);
      commit('SET_COUNTRY_SUCCESS', { country });
    } catch (error) {
      commit('SET_COUNTRY_FAILURE', error)
      throw error
    }
  },

  async loadAuthUser({ commit, dispatch }: any) {
    commit('LOAD_AUTH_USER')

    try {
      await api.auth.me();

      const user = await api.user.me()
      commit('LOAD_AUTH_USER_SUCCESS', user)
    } catch (error) {
      commit('LOAD_AUTH_USER_FAILURE', error)
      throw error
    }
  },

  async updateAuthUser({ commit }: any, updateData: any ) { //api.UserUpdateData) {
    commit('UPDATE_AUTH_USER')

    // return api
    //   .updateUser(updateData)
    //   .then(({ data }) => {
    //     commit('UPDATE_AUTH_USER_SUCCESS', data)
    //   })
    //   .catch((error) => {
    //     commit('UPDATE_AUTH_USER_FAILURE', error)

    //     throw error
    //   })
  },

  async changePassword({ commit }: any, { password }: { password: string }) {
    commit('CHANGE_PASSWORD')

    // return api
    //   .changePassword(password)
    //   .then(() => {
    //     commit('CHANGE_PASSWORD_SUCCESS')
    //   })
    //   .catch((error) => {
    //     commit('CHANGE_PASSWORD_FAILURE', error)

    //     throw error
    //   })
  },

  async updateUserName({ commit }: any, { name }: { name: string }) {
    commit('CHANGE_NAME', { name })
  },

  async updateUserMemory({ commit }:any, { amount } : { amount: number }) {
    commit('UPDATE_MEMORY', { amount })
  },

  async setUserMemory({ commit }:any, { amount } : { amount: number }) {
    commit('SET_MEMORY', { amount })
  },

  async updateShowPurchase({ commit }:any) {
    if (Cookies.get('show_purchase') === '1') {
      commit('UPDATE_SHOW_PURCHASE', true)
    }
  }
}

export default {
  state,
  getters,
  mutations,
  actions,
}
